import { useState } from 'react';
import { extractString } from '../../utils/sanitizeData';

export interface OtherDetailsFields {
  invoice_meta?: {
    invoice_id?: string | null;
    invoice_date?: string | null;
    due_date?: string | null;
    purchase_order?: string | null;
    payment_terms?: string | null;
    billing_period?: string | null;
  };
  totals?: {
    subtotal?: string | number | null;
    total_tax?: string | number | null;
    tax_rate?: string | null;
    invoice_total?: string | number | null;
    amount_due?: string | number | null;
    currency?: string | null;
  };
  additional_fields?: Record<string, unknown> | null;
  // Fallbacks from top-level fields
  po_number?: string | null;
  subtotal?: string | number | null;
  tax_amount?: string | number | null;
  tax_rate?: string | null;
  due_date?: string | null;
  invoice_date?: string | null;
  invoice_id?: string | null;
  [key: string]: unknown;
}

interface OtherDetailsCardProps {
  fields?: OtherDetailsFields;
  currency?: string;
  defaultOpen?: boolean;
}

// Convert snake_case or camelCase into Human Readable Title Case
function formatFieldKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function OtherDetailsCard({
  fields = {},
  currency = 'EUR',
  defaultOpen = true,
}: OtherDetailsCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const invoiceMeta = fields.invoice_meta || {};
  const totals = fields.totals || {};
  const additionalFields = fields.additional_fields || {};

  // Extract metadata values safely with fallbacks
  const invoiceId = extractString(
    invoiceMeta.invoice_id || fields.invoice_id || fields.invoice_number,
    '—'
  );
  const poNumber = extractString(
    invoiceMeta.purchase_order || fields.po_number || fields.purchase_order,
    '—'
  );
  const invoiceDate = extractString(
    invoiceMeta.invoice_date || fields.invoice_date,
    '—'
  );
  const dueDate = extractString(
    invoiceMeta.due_date || fields.due_date,
    '—'
  );
  const paymentTerms = extractString(
    invoiceMeta.payment_terms || fields.payment_terms,
    '—'
  );

  // Extract financial totals values safely with fallbacks
  const subtotal = extractString(
    totals.subtotal !== undefined && totals.subtotal !== null ? totals.subtotal : fields.subtotal,
    '—'
  );
  const totalTax = extractString(
    totals.total_tax !== undefined && totals.total_tax !== null ? totals.total_tax : fields.tax_amount,
    '—'
  );
  const taxRate = extractString(
    totals.tax_rate || fields.tax_rate,
    '—'
  );
  const invoiceTotal = extractString(
    totals.invoice_total !== undefined && totals.invoice_total !== null ? totals.invoice_total : fields.invoice_total,
    '—'
  );
  const amountDue = extractString(
    totals.amount_due !== undefined && totals.amount_due !== null ? totals.amount_due : fields.amount_due,
    '—'
  );
  const cur = extractString(totals.currency || currency, 'EUR');

  const formatAmount = (val: string) => {
    if (val === '—') return '—';
    return `${val} ${cur}`;
  };

  // Filter additional key-values to display
  const additionalEntries = Object.entries(additionalFields).filter(([k, v]) => {
    if (!v || v === 'null' || v === 'undefined') return false;
    // skip internal keys already rendered above
    if (['invoice_id', 'invoice_date', 'due_date', 'subtotal', 'total_tax', 'invoice_total'].includes(k)) {
      return false;
    }
    return true;
  });

  return (
    <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card mb-6 overflow-hidden">
      {/* Header / Toggle Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-brand-bg/40 dark:hover:bg-strokedark/30 transition-colors text-left cursor-pointer"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-2 rounded-full bg-brand-vib" />
          <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
            Other Details & Financial Metadata
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-brand-soft dark:text-bodydark2">
            {isOpen ? 'Collapse' : 'Expand'}
          </span>
          <svg
            className={`w-4 h-4 text-brand-soft transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Content Grid */}
      {isOpen && (
        <div className="p-5 sm:p-6 border-t border-brand-line dark:border-strokedark space-y-6 bg-brand-bg/20 dark:bg-boxdark-2/40">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {/* Column 1: Document Meta & Identifiers */}
            <div className="space-y-4 rounded-2xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-4 sm:p-5 shadow-xs">
              <div className="flex items-center gap-2 pb-2.5 border-b border-brand-line dark:border-strokedark">
                <svg className="w-3.5 h-3.5 text-brand-vib fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <h4 className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                  Document Meta & Dates
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Invoice ID / No.
                  </span>
                  <p className="font-semibold text-brand-navy dark:text-white mt-0.5 break-all">
                    {invoiceId}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Purchase Order (PO)
                  </span>
                  <p className="font-semibold text-brand-navy dark:text-white mt-0.5 break-all">
                    {poNumber}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Invoice Issue Date
                  </span>
                  <p className="font-semibold text-brand-navy dark:text-white mt-0.5">
                    {invoiceDate}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Payment Due Date
                  </span>
                  <p className="font-semibold text-brand-navy dark:text-white mt-0.5">
                    {dueDate}
                  </p>
                </div>

                {paymentTerms !== '—' && (
                  <div className="sm:col-span-2">
                    <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                      Payment Terms
                    </span>
                    <p className="font-semibold text-brand-navy dark:text-white mt-0.5">
                      {paymentTerms}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Tax & Financial Totals */}
            <div className="space-y-4 rounded-2xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-4 sm:p-5 shadow-xs">
              <div className="flex items-center gap-2 pb-2.5 border-b border-brand-line dark:border-strokedark">
                <svg className="w-3.5 h-3.5 text-brand-vib fill-current" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
                <h4 className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                  Tax & Financial Totals
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-mono">
                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Subtotal (Net)
                  </span>
                  <p className="font-semibold text-brand-navy dark:text-white mt-0.5">
                    {formatAmount(subtotal)}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Total Tax / VAT
                  </span>
                  <p className="font-semibold text-brand-navy dark:text-white mt-0.5">
                    {formatAmount(totalTax)}
                  </p>
                </div>

                {taxRate !== '—' && (
                  <div>
                    <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                      Tax / VAT Rate
                    </span>
                    <p className="font-semibold text-brand-navy dark:text-white mt-0.5">
                      {taxRate.includes('%') ? taxRate : `${taxRate}%`}
                    </p>
                  </div>
                )}

                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Invoice Grand Total
                  </span>
                  <p className="font-bold text-brand-vib dark:text-brand-300 mt-0.5">
                    {formatAmount(invoiceTotal)}
                  </p>
                </div>

                <div>
                  <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                    Amount Due
                  </span>
                  <p className="font-bold text-brand-navy dark:text-white mt-0.5">
                    {formatAmount(amountDue)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Dynamic Fields (Key-Value Metadata) */}
          {additionalEntries.length > 0 && (
            <div className="rounded-2xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-4 sm:p-5 shadow-xs space-y-3">
              <div className="flex items-center gap-2 pb-2.5 border-b border-brand-line dark:border-strokedark">
                <svg className="w-3.5 h-3.5 text-brand-vib fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <h4 className="text-[11px] font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                  Additional Metadata & Extended Fields
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs font-mono">
                {additionalEntries.map(([k, v]) => (
                  <div key={k} className="p-2.5 rounded-xl bg-brand-bg/50 dark:bg-boxdark-2/60 border border-brand-line/60 dark:border-strokedark">
                    <span className="block text-[10px] text-brand-soft dark:text-bodydark2 uppercase tracking-wider font-semibold">
                      {formatFieldKey(k)}
                    </span>
                    <p className="font-semibold text-brand-navy dark:text-white mt-1 break-all">
                      {extractString(v, '—')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
